import {Component,OnDestroy,OnInit,ViewChild} from '@angular/core';
import {ListView,TypeComparaison,TypeFilter} from "@domain/common/list-view";
import {FactureTypeCharge} from '@domain/facture/facture-type-charge';
import {FacturesTypesChargesItemComponent} from "@components/admin/frais/factures/types-charges/types-charges-item/factures-types-charges-item.component";
import {TranslateService} from '@ngx-translate/core';
import {Router} from "@angular/router";
import {environment} from '@environments/environment';
import {Sorting} from '@domain/common/list-view/sorting';
import {BehaviorSubject} from "rxjs";
import {FloatingButtonAction,TypeAction} from '@share/component/floating-button/floating-button';
import {TypeCodeErreur} from '@domain/common/http/result';
import {filter,finalize,first} from "rxjs/operators";
import {FraisFacturesService} from "@components/admin/frais/factures/frais-factures.service";
import {PleaseWaitService} from "@share/component/please-wait/please-wait.service";
import {ToastrService} from "ngx-toastr";
import {ConfirmService} from "@share/component/confirmation/confirm.service";
import {TypesChargesService} from "@services/admin/facture/types-charges.service";
import {ListViewComponent} from '@share/component/list-view/list-view.component';

/**
 * Onglet factures types charges
 *
 * @author Guillaume TRAVOT
 * @date 21/11/2023
 */
@Component({
    selector: 'factures-types-charges',
    host: {'data-test-id': 'factures-types-charges'},
    templateUrl: './factures-types-charges.component.html'
})
export class FacturesTypesChargesComponent implements OnInit {
    /** Liste view des types de charges */
    factureTypeChargeListView: ListView<FactureTypeCharge, FacturesTypesChargesItemComponent>;
    /** Liste des actions disponibles */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /** Composant de liste */
    @ViewChild('listeFacturesTypesCharges') listeFacturesTypesCharges: ListViewComponent<any,any>;

    /**
     * Constructeur
     * 
     * @param translateService Service de traduction
     * @param fraisFacturesService Service de gestion des frais factures
     * @param toastrService Service de notification
     * @param confirmService Service de confirmation
     * @param pleaseWaitService Service d'attente
     * @param router Service de routage
     * @param typesChargesService Service de gestion des types de charges
     */
    constructor(
        public translateService: TranslateService,
        private fraisFacturesService: FraisFacturesService,
        private toastrService: ToastrService,
        private confirmService: ConfirmService,
        private pleaseWaitService: PleaseWaitService,
        private router: Router,
        private typesChargesService: TypesChargesService
    ) {
    }

    /**
     * Hook initialisation
     */
    ngOnInit(): void {
        //Initialisation de la liste view
        this.factureTypeChargeListView = new ListView<FactureTypeCharge, FacturesTypesChargesItemComponent>({
            component: FacturesTypesChargesItemComponent,
            uri: `${environment.baseUrl}/controller/typesCharges`,
            title: this.translateService.instant('admin.frais.typesCharges.title'),
            defaultOrder: 'libelle',
            listeFilters: [{
                clef: 'libelle',
                title: this.translateService.instant('admin.frais.typesCharges.filtres.libelle'),
                type: TypeFilter[TypeFilter.STRING],
                typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                isDefault: true
            }, {
                clef: 'code',
                title: this.translateService.instant('admin.frais.typesCharges.filtres.code'),
                type: TypeFilter[TypeFilter.STRING],
                typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                isDefault: true
            }, {
                clef: 'idTypeFile',
                title: this.translateService.instant('admin.frais.typesCharges.filtres.formatImport'),
                typeComparaison: TypeComparaison[TypeComparaison.IN],
                multiple: true,
                listeValues: this.typesChargesService.typesFiles.map(typeFile => ({
                    code: typeFile.id.toString(),
                    libelle: typeFile.libelle
                }))
            }],
            isFilter: true
        });

        //Définition des colonnes de tri
        this.factureTypeChargeListView.columns = [
            {key: 'libelle', title: this.translateService.instant('admin.frais.typesCharges.filtres.libelle')},
            {key: 'code', title: this.translateService.instant('admin.frais.typesCharges.tooltip.code')},
            {key: 'formatImport', title: this.translateService.instant('admin.frais.typesCharges.tooltip.formatImport')}
        ];

        //Ajout du tri de la liste selon l'ordre voulu
        this.factureTypeChargeListView.sorting = new Sorting(
            this.factureTypeChargeListView.columns,
            'libelle'
        );

        //Nom de la classe
        this.factureTypeChargeListView.className = "factures-types-charges";

        //Persistence des filtres
        this.factureTypeChargeListView.isPersistFilters = true;

        //Définition de la liste des actions possibles
        this.listeActions.next([
            {
                type: TypeAction.PRIMARY,
                icone: 'nio icon-ajouter',
                libelle: 'admin.frais.typesCharges.action.ajouter',
                doAction: () => this.creerTypesCharges()
            },{
                type: TypeAction.PRIMARY,
                icone: 'nio icon-emission',
                libelle: 'admin.frais.typesCharges.action.supprimer',
                isVisible: () => this.factureTypeChargeListView.listeObjetsSectionnes?.length > 0,
                doAction: () => this.deleteTypesCharges()
            },{
                type: TypeAction.SECONDARY,
                icone: 'nio icon-tout_selectionner',
                libelle: 'liste.actions.selectionnerElementsAffiches',
                isVisible: () => this.factureTypeChargeListView.listeObjetsSectionnes?.length > 0,
                doAction: () => this.factureTypeChargeListView.selectAll(true),
            },{
                type: TypeAction.SECONDARY,
                icone: 'nio icon-annuler_selection',
                libelle: 'liste.actions.toutDeselectionner',
                isVisible: () => this.factureTypeChargeListView.listeObjetsSectionnes?.length > 0,
                doAction: () => this.factureTypeChargeListView.selectAll(false)
            }
        ]);
    }

    /**
     * Suppression des types de charges sélectionnées
     */
    deleteTypesCharges(): void {
        //Affichage de la confirmation de suppression du type de charge
        this.confirmService.showConfirm(this.translateService.instant('admin.frais.typesCharges.action.confirmDeleteFraisCharge'))
            .pipe(filter(isConfirmed => isConfirmed))
            .subscribe({
                next: () => {
                    //S'il y a bien des éléments sélectionnés
                    if (this.factureTypeChargeListView.listeObjetsSectionnes?.length > 0) {
                        //Affichage de la popup d'attente
                        let waitDialogRef = this.pleaseWaitService.show();

                        //Suppression des types de charges sélectionnés
                        this.fraisFacturesService.deleteTypesCharges(this.factureTypeChargeListView.listeObjetsSectionnes)
                            .pipe(first(), finalize(() => {
                                waitDialogRef.close();
                            }))
                            .subscribe(result => {
                                //Refresh de la liste
                                this.factureTypeChargeListView.refresh();

                                //S'il n'y a pas d'erreur
                                if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                                    //Message d'information
                                    this.toastrService.success(this.translateService.instant('admin.frais.typesCharges.action.confirmationDeleteFraisCharge'));
                                } else {
                                    //Message d'erreur
                                    TypeCodeErreur.showError(result?.codeErreur, this.translateService, this.toastrService);
                                }
                            });
                    }
                }
            });
    }

    /**
     * Destruction
     */
    ngOnDestroy() {
        //Indique au composant Frais Factures qu'on n'affiche plus le détail d'un type de charge
        this.fraisFacturesService.isShowDetail = false;
    }

    /**
     * Méthode appelée lors de la création d'un nouveau type de charge
     */
    creerTypesCharges(): void {
        this.router.navigate(['Admin/Frais/Factures/TypesCharges/0']);
    }
}
